import { rAPI } from './rAPI'
import {
  OrganisationWithBilling,
  BillingDetail,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreatePortalSessionRequest,
  CreatePortalSessionResponse,
  UpdateUserLicenseRequest,
  UpdateUserLicenseResponse,
  GetInvoicesResponse,
  PreviewLicenseChangeRequest,
  PreviewLicenseChangeResponse,
} from './types'

export const billingAPISlice = rAPI.enhanceEndpoints({
  addTagTypes: ['BillingStatus', 'OrganisationBilling', 'BillingDetail'],
}).injectEndpoints({
  overrideExisting: false,

  endpoints: (builder) => ({
    /**
     * Get billing status for an organisation
     * Returns OrganisationWithBilling (includes billing details)
     * @param orgId - Organisation ID
     * @param sync - Optional: if true, syncs from Stripe before returning status
     */
    getBillingStatus: builder.query<OrganisationWithBilling, { orgId: number; sync?: boolean }>({
      query: ({ orgId, sync }) => {
        const url = `/organisations/${orgId}/billing/status`
        return sync ? `${url}?sync=true` : url
      },
      providesTags: (result, error, { orgId }) => [
        { type: 'BillingStatus', id: orgId },
        { type: 'OrganisationBilling', id: orgId },
      ],
    }),

    /**
     * Sync billing status from Stripe
     * Manually triggers a sync of subscription data from Stripe
     */
    syncBillingStatus: builder.mutation<OrganisationWithBilling, number>({
      query: (orgId) => ({
        url: `/organisations/${orgId}/billing/sync`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, orgId) => [
        { type: 'BillingStatus', id: orgId },
        { type: 'OrganisationBilling', id: orgId },
      ],
    }),

    /**
     * Resume a cancelled subscription
     * Resumes a subscription that is in grace period (cancellation_pending)
     */
    resumeSubscription: builder.mutation<OrganisationWithBilling, number>({
      query: (orgId) => ({
        url: `/organisations/${orgId}/billing/resume`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, orgId) => [
        { type: 'BillingStatus', id: orgId },
        { type: 'OrganisationBilling', id: orgId },
      ],
    }),

    /**
     * Create a Stripe checkout session for subscription
     */
    createCheckoutSession: builder.mutation<
      CreateCheckoutSessionResponse,
      CreateCheckoutSessionRequest
    >({
      query: ({ organisationId, plan, success_url, cancel_url }) => ({
        url: `/organisations/${organisationId}/checkout`,
        method: 'POST',
        body: {
          plan,
          success_url,
          cancel_url,
        },
      }),
      invalidatesTags: (result, error, { organisationId }) => [
        { type: 'BillingStatus', id: organisationId },
        { type: 'OrganisationBilling', id: organisationId },
      ],
    }),

    /**
     * Create a Stripe customer portal session
     */
    createPortalSession: builder.mutation<
      CreatePortalSessionResponse,
      CreatePortalSessionRequest
    >({
      query: ({ organisationId, return_url }) => ({
        url: `/organisations/${organisationId}/billing/portal`,
        method: 'POST',
        body: {
          return_url,
        },
      }),
    }),

    /**
     * Update a user's license within an organisation
     */
    updateUserLicense: builder.mutation<
      UpdateUserLicenseResponse,
      UpdateUserLicenseRequest
    >({
      query: ({ organisationId, userId, license_type }) => ({
        url: `/organisations/${organisationId}/users/${userId}/license`,
        method: 'PATCH',
        body: {
          license_type,
        },
      }),
      invalidatesTags: (result, error, { organisationId }) => [
        { type: 'BillingStatus', id: organisationId },
        { type: 'OrganisationBilling', id: organisationId },
        { type: 'User', id: result?.user_id },
        'Users', // Invalidate team members list to refresh license status
      ],
    }),

    /**
     * Get invoice history for an organisation
     */
    getInvoices: builder.query<GetInvoicesResponse, number>({
      query: (orgId) => `/organisations/${orgId}/billing/invoices`,
      providesTags: (result, error, orgId) => [
        { type: 'BillingStatus', id: orgId },
        { type: 'OrganisationBilling', id: orgId },
      ],
    }),

    /**
     * Preview the prorated cost of changing a user's license
     */
    previewUserLicenseUpdate: builder.mutation<
      PreviewLicenseChangeResponse,
      PreviewLicenseChangeRequest
    >({
      query: ({ organisationId, userId, license_type }) => ({
        url: `/organisations/${organisationId}/users/${userId}/license/preview`,
        method: 'POST',
        body: { license_type },
      }),
    }),

    /**
     * Get billing contact details for an organisation
     */
    getBillingDetail: builder.query<BillingDetail | null, number>({
      query: (orgId) => `/organisations/${orgId}/billing/contact`,
      providesTags: (result, error, orgId) => [
        { type: 'BillingDetail', id: orgId },
        { type: 'BillingStatus', id: orgId },
        { type: 'OrganisationBilling', id: orgId },
      ],
    }),

    /**
     * Update billing contact details for an organisation
     */
    updateBillingDetail: builder.mutation<
      BillingDetail,
      { organisationId: number; payload: Partial<BillingDetail> }
    >({
      query: ({ organisationId, payload }) => ({
        url: `/organisations/${organisationId}/billing/contact`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, { organisationId }) => [
        { type: 'BillingDetail', id: organisationId },
        { type: 'BillingStatus', id: organisationId },
        { type: 'OrganisationBilling', id: organisationId },
        'Organisations', // Invalidate organisation cache to refresh billing_detail
      ],
    }),
  }),
})

export const {
  useGetBillingStatusQuery,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
  useUpdateUserLicenseMutation,
  useGetInvoicesQuery,
  usePreviewUserLicenseUpdateMutation,
  useGetBillingDetailQuery,
  useUpdateBillingDetailMutation,
  useSyncBillingStatusMutation,
  useResumeSubscriptionMutation,
} = billingAPISlice

